import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ComponentVariant } from '../types'

@customElement('e-button')
export class EButton extends LitElement {
    /* Use the common type for variant so that allowed values are enforced */
    @property({ type: String, reflect: true }) variant: ComponentVariant = 'accent'

    @property({ type: String }) size: 'small' | 'medium' | 'large' = 'medium'
    @property({ type: Boolean, reflect: true }) fullWidth = false

    static styles = css`
        :host {
            display: inline-flex;
            position: relative;
            overflow: hidden;
        }

        :host([fullWidth]) button {
            width: 100%;
        }

        button {
            font-family: var(--base-font-family, inherit);
            font-size: var(--font-size-button);
            font-weight: 500;
            padding: var(--s0) var(--s2);
            border: none;
            border-radius: var(--radius-medium);
            cursor: pointer;
            position: relative;
            transition:
                transform var(--duration-fast) var(--ease-in-out),
                box-shadow var(--duration-fast) var(--ease-in-out),
                color var(--duration-fast) var(--ease-in-out);
            white-space: nowrap;
            width: auto;
            box-sizing: border-box;
            height: var(--control-height);
            z-index: 1;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        button:hover {
            transform: scale(1.05);
            box-shadow: 0 7px 10px rgba(0, 0, 0, 0.15);
        }

        button:active {
            transform: scale(0.98);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        /* Pseudo-elements for gradient and shine effects */
        button::before {
            content: '';
            position: absolute;
            inset: 0;
            z-index: -1;
            background: linear-gradient(to right, var(--gradient-start), var(--gradient-end));
            opacity: 0;
            transition: opacity var(--duration-normal) var(--ease-in-out);
        }

        button:hover::before {
            opacity: 1;
        }

        button::after {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 50%;
            height: 100%;
            background: linear-gradient(
                to right,
                rgba(255, 255, 255, 0) 0%,
                rgba(255, 255, 255, 0.3) 50%,
                rgba(255, 255, 255, 0) 100%
            );
            transform: skewX(-25deg);
        }

        button:hover::after {
            animation: shine var(--duration-slow) forwards;
        }

        @keyframes shine {
            100% {
                left: 150%;
            }
        }

        /* Variant styles */
        button.variant-accent {
            background-color: var(--color-accent);
            color: white;
        }

        button.variant-playful {
            background-color: var(--color-playful);
            color: white;
        }

        button.variant-gradient {
            background: linear-gradient(to right, var(--gradient-start), var(--gradient-end));
            color: white;
        }

        button:focus {
            outline: none;
            box-shadow: 0 0 0 2px var(--primary-color);
        }
    `

    render() {
        const buttonClasses = [`size-${this.size}`, `variant-${this.variant}`].join(' ')
        return html`
            <button class="${buttonClasses}">
                <slot></slot>
            </button>
        `
    }
}
